import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
    CheckCircle, Clock, Search, Video, Mail, 
    ArrowRight, Loader2, AlertCircle, ShieldCheck,
    Calendar, UserCheck, MessageSquare, X
} from 'lucide-react';
import axios from 'axios';

const LecturerPendingVerification = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const email = searchParams.get('email');
    const [statusData, setStatusData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [institution, setInstitution] = useState(null);

    useEffect(() => {
        const storedInstitution = localStorage.getItem('selectedInstitution');
        if (storedInstitution) {
            setInstitution(JSON.parse(storedInstitution));
        }

        if (!email) {
            navigate('/lecturer/apply');
            return;
        }

        const fetchStatus = async () => {
            try {
                const response = await axios.get(`/lecturers/application/status/${email}`);
                setStatusData(response.data);
                setError('');
            } catch (err) {
                console.error(err);
                setError('Failed to fetch application status.');
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
        const interval = setInterval(fetchStatus, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, [email, navigate]);

    const handleJoinInterview = async () => {
        try {
            const response = await axios.get(`/lecturers/applications/${statusData.applicationId}/interview-token?email=${email}`);
            const { token, roomName } = response.data;
            // In a real app, you'd navigate to a video room or open a LiveKit modal
            // For now, let's just log and maybe show a success message
            console.log('Joining room:', roomName, 'with token:', token);
            alert(`Opening virtual interview room: ${roomName}`);
            // navigate(`/virtual-interview?token=${token}&room=${roomName}`);
        } catch (err) {
            console.error(err);
            alert('Failed to join interview. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                    <p className="text-gray-600 font-bold animate-pulse">Synchronizing Application Data...</p>
                </div>
            </div>
        );
    }
 
    const timeline = [
        { title: 'Application Received', status: 'completed', desc: 'Your details have been successfully submitted.' },
        { title: 'Initial Review', status: statusData.status === 'pending' ? 'current' : 'completed', desc: 'Our team is reviewing your qualifications and profile.' },
        { 
            title: 'Document Verification', 
            status: statusData.documentStatus && Object.values(statusData.documentStatus).some(d => d.status === 'rejected') ? 'current' : (['pending', 'interview_scheduled'].includes(statusData.status) ? (statusData.status === 'pending' ? 'pending' : 'completed') : 'completed'), 
            desc: statusData.documentStatus && Object.values(statusData.documentStatus).some(d => d.status === 'rejected') ? 'Action required: Some documents need attention.' : 'Verifying certificates and identity documents.' 
        },
        { title: 'Virtual Interview', status: statusData.status === 'interview_scheduled' ? 'current' : (statusData.status === 'approved' ? 'completed' : 'pending'), desc: 'One-on-one session with our academic board.' },
        { title: 'Final Approval', status: statusData.status === 'approved' ? 'completed' : 'pending', desc: 'Final decision and account activation.' }
    ];
 
    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex flex-col items-center py-12 px-4 selection:bg-blue-500">
            {/* Header */}
            <div className="w-full max-w-4xl text-center mb-12">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-200">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h1 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">Application Received!</h1>
                <p className="text-gray-600 max-w-xl mx-auto">
                    Thank you for applying to <span className="text-blue-600 font-bold">{institution?.name || 'our institution'}</span>. 
                    Your application is currently under review by our academic panel.
                </p>
            </div>
 
            <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Timeline Column */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white border border-gray-200 shadow-xl rounded-3xl p-8">
                        <h2 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            <Clock className="w-6 h-6 text-blue-600" /> Onboarding Timeline
                        </h2>
                        
                        <div className="space-y-8 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                            {timeline.map((item, i) => (
                                <div key={i} className="flex gap-6 relative">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                                        item.status === 'completed' ? 'bg-emerald-500 shadow-lg' : 
                                        item.status === 'current' ? 'bg-blue-600 animate-pulse shadow-lg' : 
                                        'bg-gray-100 border border-gray-200'
                                    }`}>
                                        {item.status === 'completed' && <CheckCircle className="w-4 h-4 text-white" />}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className={`font-bold ${item.status === 'pending' ? 'text-gray-300' : 'text-gray-900'}`}>{item.title}</h3>
                                        <p className="text-sm text-gray-500">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Document Feedback Section */}
                    {statusData.documentStatus && Object.values(statusData.documentStatus).some(d => d.status === 'rejected') && (
                        <div className="bg-red-50 border border-red-100 shadow-lg rounded-3xl p-8 animate-in slide-in-from-bottom duration-500">
                            <h2 className="text-xl font-bold text-red-900 mb-6 flex items-center gap-3">
                                <AlertCircle className="w-6 h-6" /> Document Action Required
                            </h2>
                            <div className="space-y-4">
                                {Object.entries(statusData.documentStatus).map(([key, data]) => (
                                    data.status === 'rejected' && (
                                        <div key={key} className="p-4 bg-white border border-red-200 rounded-2xl flex justify-between items-center shadow-sm">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                                    <X className="w-5 h-5 text-red-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900 capitalize">{key.replace('cert-', 'Certificate ').replace('idDocument', 'ID Document')}</p>
                                                    <p className="text-xs text-red-600 font-medium">Issue: {data.reason || 'Incorrect or unreadable file.'}</p>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => navigate('/lecturer/apply')}
                                                className="px-4 py-2 bg-red-600 text-white text-xs font-black rounded-xl hover:bg-red-700 transition-all shadow-md shadow-red-600/20"
                                            >
                                                FIX NOW
                                            </button>
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white border border-gray-100 shadow-lg rounded-2xl p-6 flex gap-4">
                            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                                <Search className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <h4 className="text-gray-900 font-bold text-sm">Review Process</h4>
                                <p className="text-xs text-gray-500 mt-1">Typical review window is 3-5 business days.</p>
                            </div>
                        </div>
                        <div className="bg-white border border-gray-100 shadow-lg rounded-2xl p-6 flex gap-4">
                            <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center shrink-0">
                                <Mail className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <h4 className="text-gray-900 font-bold text-sm">Credential Delivery</h4>
                                <p className="text-xs text-gray-500 mt-1">Once approved, check your email for login details.</p>
                            </div>
                        </div>
                    </div>
                </div>
 
                {/* Status Column */}
                <div className="space-y-6">
                    {/* Interview Panel */}
                    <div className="bg-white border border-gray-200 shadow-xl rounded-3xl p-8 relative overflow-hidden group">
                        <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-50 rounded-full blur-3xl group-hover:bg-blue-100 transition-all"></div>
                        
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Application Status</h2>
                        
                        <div className="mb-8">
                            <span className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border ${
                                statusData.status === 'approved' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' :
                                statusData.status === 'rejected' ? 'bg-red-50 border-red-100 text-red-600' :
                                statusData.status === 'interview_scheduled' ? 'bg-blue-50 border-blue-100 text-blue-600' :
                                'bg-amber-50 border-amber-100 text-amber-600'
                            }`}>
                                {statusData.status.replace('_', ' ')}
                            </span>
                        </div>
 
                        {statusData.status === 'interview_scheduled' ? (
                            <div className="space-y-6 animate-in zoom-in duration-500">
                                <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl">
                                    <p className="text-blue-700 text-sm leading-relaxed">
                                        Your virtual interview has been scheduled. Please wait for the panel to activate the room.
                                    </p>
                                </div>
                                
                                {statusData.interviewIsActive ? (
                                    <button 
                                        onClick={handleJoinInterview}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-3 shadow-lg shadow-emerald-600/20 animate-pulse"
                                    >
                                        <Video className="w-5 h-5" /> JOIN INTERVIEW NOW
                                    </button>
                                ) : (
                                    <div className="w-full bg-gray-50 border border-gray-200 text-gray-400 font-bold py-4 rounded-2xl flex items-center justify-center gap-3">
                                        <Loader2 className="w-5 h-5 animate-spin" /> WAITING FOR PANEL...
                                    </div>
                                )}
                            </div>
                        ) : statusData.status === 'rejected' ? (
                            <div className="space-y-4 animate-in fade-in duration-500">
                                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl">
                                    <h4 className="text-red-600 font-bold text-xs uppercase mb-2">Rejection Reason</h4>
                                    <p className="text-red-700 text-sm">{statusData.rejectionReason || 'No specific reason provided.'}</p>
                                </div>
                                <button onClick={() => navigate('/lecturer/apply')} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold py-3 rounded-xl transition-all">
                                    Re-apply
                                </button>
                            </div>
                        ) : statusData.status === 'approved' ? (
                            <div className="space-y-4 animate-in fade-in duration-500 text-center">
                                <UserCheck className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                                <p className="text-emerald-700 text-sm font-medium">Your account is ready! Please check your email for your autogenerated credentials.</p>
                                <button onClick={() => navigate('/login?role=lecturer')} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2">
                                    Go to Login <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 text-center">
                                <ShieldCheck className="w-10 h-10 text-blue-300 mx-auto mb-4" />
                                <p className="text-gray-400 text-sm">Waiting for document verification results...</p>
                            </div>
                        )}
                    </div>
 
                    {/* Support Panel */}
                    <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-3xl p-8 shadow-xl shadow-blue-600/20">
                        <h3 className="text-white font-bold mb-4">Need Assistance?</h3>
                        <p className="text-white/70 text-sm mb-6">If you have any questions regarding the onboarding process, reach out to our team.</p>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 text-white">
                                <MessageSquare className="w-5 h-5 text-blue-200" />
                                <span className="text-sm font-medium">WhatsApp Support</span>
                            </div>
                            <div className="flex items-center gap-3 text-white">
                                <Calendar className="w-5 h-5 text-blue-200" />
                                <span className="text-sm font-medium">Request Callback</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LecturerPendingVerification;
